// src/hooks/useFreelancerProjects.js
// Hook to manage freelancer project assignments
import { useState, useEffect, useCallback } from "react";
import { getFreelancerAssignedProjects, getAllTeamMembers } from "../services/azureTeamService";
import { isFreelancer } from "../utils/permissions";

export const useFreelancerProjects = (user) => {
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFreelancerProjects = useCallback(async () => {
    if (!user || !isFreelancer(user)) {
      setAssignedProjects([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the user's email to find their freelancer record
      const userEmail = user.email || user.userDetails || user.name;

      // First try to get from user object if available (dev mode)
      if (user.assignedProjects && user.assignedProjects.length > 0) {
        setAssignedProjects(user.assignedProjects);
        setLoading(false);
        return;
      }

      // Otherwise, fetch from team service
      const projects = await getFreelancerAssignedProjects(userEmail);
      setAssignedProjects(projects);
    } catch (err) {
      console.error("Error loading freelancer projects:", err);
      setError(err.message);
      setAssignedProjects([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadFreelancerProjects();
  }, [loadFreelancerProjects]);

  // Check if a project code is accessible to this freelancer
  const canAccessProject = useCallback((projectCode) => {
    // If not a freelancer, they can access all projects (subject to other permissions)
    if (!user || !isFreelancer(user)) {
      return true;
    }
    // Freelancers can only access assigned projects
    return assignedProjects.includes(projectCode);
  }, [user, assignedProjects]);

  // Filter an array of projects to only those accessible to freelancer
  const filterProjects = useCallback((projects) => {
    if (!user || !isFreelancer(user)) {
      return projects;
    }
    return projects.filter((project) => assignedProjects.includes(project.code));
  }, [user, assignedProjects]);

  return {
    assignedProjects,
    loading,
    error,
    canAccessProject,
    filterProjects,
    isFreelancerUser: user ? isFreelancer(user) : false,
    refresh: loadFreelancerProjects,
  };
};

export default useFreelancerProjects;
